import type { LandingCopy } from './types';

/**
 * Copy PT-BR da landing page Omanna.
 *
 * Source-of-truth textual unica — JSX nunca pode ter string hardcoded
 * (clean-code §13/§3 e PRD §9). Strings travadas pelo UI-SPEC Phase 1
 * (Copywriting Contract): sem `!` em CTAs, sem mistura PT/EN, CTAs comecam
 * por verbo no infinitivo, subtexto e uma sentenca apenas.
 *
 * Numero do WhatsApp NAO mora aqui — vem de `import.meta.env.VITE_WHATSAPP_NUMBER`
 * em runtime (substituicao build-time pelo Vite). Ver TODO markers abaixo.
 */

// TODO(phase6): whatsappPhone vem de import.meta.env.VITE_WHATSAPP_NUMBER (pendente — PRD §14 item 1)
// TODO(phase6,review): defaultMessage pode ser revisada por Lucas antes do go-live (PRD §14 item 2)
export const landingCopy: LandingCopy = {
  hero: {
    headline: 'A Omanna desenvolve software sob medida para pequenas e médias empresas.',
    subtitle: 'Sistemas claros, rápidos e feitos sob a realidade do seu negócio.',
    primaryCtaLabel: 'Falar no WhatsApp',
    primaryCtaAriaLabel: 'Falar no WhatsApp com a Omanna — abre em nova aba',
    secondaryCtaLabel: 'Ver como funciona',
    secondaryCtaAriaLabel: 'Ver como funciona — rolar até a seção de processo',
    mockupAlt:
      'Painel de exemplo de um sistema Omanna mostrando indicadores e gráficos do negócio.',
  },
  navbar: {
    logoAriaLabel: 'Omanna — Provisão em Software (voltar ao topo)',
    menuItems: [
      { label: 'Serviços', href: '#servicos' },
      { label: 'Como funciona', href: '#como-funciona' },
      { label: 'Sobre', href: '#quem-somos' },
      { label: 'FAQ', href: '#faq' },
    ],
    menuToggleAriaLabel: 'Abrir menu de navegação',
    ctaLabel: 'Falar no WhatsApp',
    ctaAriaLabel: 'Falar no WhatsApp com a Omanna a partir do menu de navegação — abre em nova aba',
  },
  floatingCta: {
    ariaLabel: 'Falar no WhatsApp com a Omanna pelo botão flutuante — abre em nova aba',
  },
  quemSomos: {
    eyebrow: 'Quem somos',
    title: 'Provisão em software para quem precisa crescer.',
    paragraphs: [
      {
        iconKey: 'origin',
        lead: 'O nome veio do maná.',
        body: 'O nome Omanna vem do maná — a provisão que chegava exatamente na hora da necessidade. É essa a promessa da nossa marca: entregar a tecnologia certa, no momento certo, para o seu negócio crescer.',
      },
      {
        iconKey: 'purpose',
        lead: 'Toda PME merece tecnologia do lado dela.',
        body: 'A Omanna nasceu pra resolver o que mais trava as pequenas e médias empresas: processo manual, planilha que não conversa entre si, sistema caro que não se encaixa na rotina. Acreditamos que toda empresa, por menor que seja, merece ter tecnologia do seu lado.',
      },
      {
        iconKey: 'founder',
        lead: 'Liderada por quem desenvolve.',
        body: 'Liderada por Lucas Possebon, desenvolvedor com experiência em sistemas de grandes empresas, focado em trazer essa mesma qualidade para empresas que estão crescendo e precisam de tecnologia que funciona de verdade.',
      },
    ],
  },
  problema: {
    eyebrow: 'O problema',
    title: 'Sua empresa ainda funciona assim?',
    items: [
      {
        iconKey: 'spreadsheet',
        text: 'Planilhas que ninguém entende e que viram caos quando a equipe cresce.',
      },
      {
        iconKey: 'lock',
        text: 'Sistema engessado, caro e cheio de coisas que você nunca vai usar.',
      },
      {
        iconKey: 'manual',
        text: 'Atendimento, agenda e cadastro feitos no manual, no improviso.',
      },
      {
        iconKey: 'scattered',
        text: 'Informação espalhada em vários lugares — relatório virou pesadelo.',
      },
    ],
    transition: 'A Omanna existe pra acabar com isso.',
  },
  servicos: {
    eyebrow: 'Serviços',
    title: 'O que entregamos.',
    cards: [
      {
        iconKey: 'sistemas',
        title: 'Sistemas sob medida',
        description: 'Desenhados pra realidade do seu negócio, do zero ou sobre o que você já tem.',
      },
      {
        iconKey: 'automacoes',
        title: 'Automações e integrações',
        description: 'WhatsApp, agendas, planilhas, e-mail — tudo conversando entre si.',
      },
      {
        iconKey: 'paineis',
        title: 'Painéis e relatórios',
        description: 'Informação clara, na hora, sem precisar montar planilha.',
      },
      {
        iconKey: 'suporte',
        title: 'Suporte de verdade',
        description: 'Você fala com quem fez o sistema. Sem robô, sem fila de atendimento.',
      },
    ],
  },
  comoFunciona: {
    eyebrow: 'Como funciona',
    title: 'Simples assim.',
    steps: [
      {
        number: '01',
        title: 'Conversamos.',
        description: 'Você conta seu problema. A gente escuta e entende a sua rotina.',
      },
      {
        number: '02',
        title: 'Desenhamos.',
        description: 'Apresentamos uma proposta com solução, prazo e investimento claros.',
      },
      {
        number: '03',
        title: 'Desenvolvemos.',
        description: 'Você acompanha cada etapa — sem caixa-preta, sem surpresa.',
      },
      {
        number: '04',
        title: 'Entregamos e apoiamos.',
        description: 'Sistema no ar e a gente continua do seu lado.',
      },
    ],
  },
  diferenciais: {
    eyebrow: 'Diferenciais',
    title: 'Por que escolher a Omanna.',
    items: [
      {
        lead: 'Atendimento direto com quem desenvolve.',
        body: 'Você fala com quem fez o sistema — sem passar por atendente, sem explicar tudo de novo.',
      },
      {
        lead: 'Tecnologia atual, sem dependência.',
        body: 'Seu sistema não fica preso a um fornecedor. Se um dia a gente não se encaixar mais, você leva tudo.',
      },
      {
        lead: 'Preço justo pra realidade da PME.',
        body: 'Cobramos pelo que entrega valor, não por hora cheia.',
      },
      {
        lead: 'Foco no problema real.',
        body: 'A gente resolve a dor, não vende pacote.',
      },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Perguntas que ouvimos sempre.',
    entries: [
      {
        question: 'Quanto custa um sistema sob medida?',
        answer:
          'Depende do problema. Mas a gente desenha a proposta antes de qualquer compromisso — sem custo.',
      },
      {
        question: 'Quanto tempo demora pra ficar pronto?',
        answer:
          'A primeira versão útil costuma sair em 4 a 8 semanas. A gente entrega em fases, então você usa antes do "fim".',
      },
      {
        question: 'E se eu não souber explicar direito o que preciso?',
        answer:
          'É exatamente pra isso que a gente existe. Você conta o problema, a gente desenha a solução.',
      },
      {
        question: 'E depois que entrega? Fico na mão?',
        answer:
          'Não. Todo projeto inclui período de suporte, e a maioria dos clientes continua com manutenção mensal.',
      },
      {
        question: 'Vocês trabalham com qual tipo de empresa?',
        answer:
          'Empresas de qualquer segmento que ainda dependem de processo manual, planilha ou sistema que não atende direito.',
      },
      {
        question: 'Eu já tenho um sistema. Dá pra melhorar?',
        answer:
          'Quase sempre dá. Integramos, automatizamos ou substituímos por algo que se encaixa melhor — sem perder o que já funciona.',
      },
    ],
  },
  ctaFinal: {
    title: 'Vamos conversar sobre o que sua empresa realmente precisa?',
    subtitle: 'Sem compromisso, sem proposta forçada — só uma conversa pra entender o problema.',
    buttonLabel: 'Falar no WhatsApp',
    buttonAriaLabel: 'Falar no WhatsApp com a Omanna a partir do CTA final — abre em nova aba',
  },
  footer: {
    tagline: 'Provisão em Software',
    description: 'Tecnologia que organiza. Soluções que impulsionam.',
    contactHeading: 'Contato',
    contactItems: [
      {
        iconKey: 'whatsapp',
        label: 'WhatsApp',
        // Caller envolve em <a href> via whatsappLink; aqui o href fica como TODO label
        // (substituido em runtime no Footer.tsx)
        href: 'wa.me',
        external: true,
      },
      {
        // TODO(phase6,review): confirmar email oficial (PRD §14 item 2)
        iconKey: 'email',
        label: 'contato@omanna.com.br',
        href: 'mailto:contato@omanna.com.br',
        external: false,
      },
      {
        iconKey: 'instagram',
        label: '@omannatech',
        href: 'https://instagram.com/omannatech',
        external: true,
      },
    ],
    navHeading: 'Navegação',
    navLinks: [
      { label: 'Serviços', href: '#servicos' },
      { label: 'Como funciona', href: '#como-funciona' },
      { label: 'Sobre', href: '#quem-somos' },
      { label: 'FAQ', href: '#faq' },
    ],
    copyright: '© 2026 Omanna. Todos os direitos reservados.',
  },
  whatsapp: {
    defaultMessage: 'Olá! Vim pelo site da Omanna e gostaria de conversar sobre um projeto.',
  },
} as const;
